import {
  useInventoryKpis,
  useInventoryPerformance,
  useInventoryLocationValues,
  useInventoryLowStock,
  useInventoryRecentActivity,
} from '@hooks/inventory';
import { useAppSelector } from '@hooks/useRedux';
import { EmptyState } from '@components/common';
import { selectCurrentUser } from '@store/auth/authSelectors';
import { selectEffectiveOutlet } from '@store/outlets';
import React, { useEffect, useMemo, useState } from 'react';
import { InventoryDashboardKpiRow } from './InventoryDashboardKpiRow';
import { InventoryDashboardLocationValueCard } from './InventoryDashboardLocationValueCard';
import { InventoryDashboardLowStockCard } from './InventoryDashboardLowStockCard';
import { InventoryDashboardPerformanceCard } from './InventoryDashboardPerformanceCard';
import { InventoryDashboardRecentActivityCard } from './InventoryDashboardRecentActivityCard';
import { InventoryDashboardToolbar } from './InventoryDashboardToolbar';
import {
  getInventoryDashboardRange,
  InventoryDashboardPeriodPreset,
} from './inventoryDashboard';

export function InventoryDashboardPageContent() {
  const currentUser = useAppSelector(selectCurrentUser);
  const effectiveOutlet = useAppSelector(selectEffectiveOutlet);
  const companyId = currentUser?.company?.id ?? null;
  const outletId = effectiveOutlet?.id ?? null;

  const [preset, setPreset] =
    useState<InventoryDashboardPeriodPreset>('last_30_days');
  const [dateRange, setDateRange] = useState(() =>
    getInventoryDashboardRange('last_30_days'),
  );

  useEffect(() => {
    setDateRange(getInventoryDashboardRange(preset));
  }, [preset]);

  const scopeLabel = useMemo(() => {
    if (!effectiveOutlet) {
      return 'Select an outlet to review stock, purchasing, and movement performance.';
    }

    return `${effectiveOutlet.name} inventory health, stock position, and supplier activity.`;
  }, [effectiveOutlet]);

  const kpisQuery = useInventoryKpis(companyId, outletId);
  const performanceQuery = useInventoryPerformance(
    companyId,
    outletId,
    dateRange.dateFrom,
    dateRange.dateTo,
  );
  const locationValuesQuery = useInventoryLocationValues(companyId, outletId);
  const lowStockQuery = useInventoryLowStock(companyId, outletId);
  const recentActivityQuery = useInventoryRecentActivity(
    companyId,
    outletId,
    dateRange.dateFrom,
    dateRange.dateTo,
  );

  const handleRefresh = () => {
    kpisQuery.refetch();
    performanceQuery.refetch();
    locationValuesQuery.refetch();
    lowStockQuery.refetch();
    recentActivityQuery.refetch();
  };

  const isRefreshing =
    kpisQuery.isFetching ||
    performanceQuery.isFetching ||
    locationValuesQuery.isFetching ||
    lowStockQuery.isFetching ||
    recentActivityQuery.isFetching;

  const snapshot = useMemo(() => {
    return {
      overview: kpisQuery.data ?? null,
      movements: performanceQuery.data?.movements ?? [],
      purchases: performanceQuery.data?.purchases ?? [],
      lowStock: lowStockQuery.data ?? [],
      locationValues: locationValuesQuery.data ?? [],
      recentActivity: recentActivityQuery.data ?? [],
    };
  }, [
    kpisQuery.data,
    performanceQuery.data,
    lowStockQuery.data,
    locationValuesQuery.data,
    recentActivityQuery.data,
  ]);

  if (!outletId) {
    return (
      <div className="space-y-4">
        <InventoryDashboardToolbar
          scopeLabel={scopeLabel}
          preset={preset}
          dateFrom={dateRange.dateFrom}
          dateTo={dateRange.dateTo}
          isRefreshing={false}
          onPresetChange={setPreset}
          onDateFromChange={(value) =>
            setDateRange((current) => ({ ...current, dateFrom: value }))
          }
          onDateToChange={(value) =>
            setDateRange((current) => ({ ...current, dateTo: value }))
          }
          onRefresh={() => {}}
        />
        <EmptyState
          title="Choose an outlet first"
          description="The inventory dashboard is outlet-aware because stock balances, purchase receipts, and stock locations are all tracked per outlet."
          icon="package"
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <InventoryDashboardToolbar
        scopeLabel={scopeLabel}
        preset={preset}
        dateFrom={dateRange.dateFrom}
        dateTo={dateRange.dateTo}
        isRefreshing={isRefreshing}
        onPresetChange={setPreset}
        onDateFromChange={(value) =>
          setDateRange((current) => ({ ...current, dateFrom: value }))
        }
        onDateToChange={(value) =>
          setDateRange((current) => ({ ...current, dateTo: value }))
        }
        onRefresh={handleRefresh}
      />

      <div
        className={
          kpisQuery.isLoading
            ? 'animate-pulse opacity-60 pointer-events-none'
            : ''
        }
      >
        <InventoryDashboardKpiRow snapshot={snapshot} />
      </div>

      <div className="grid gap-4 xl:grid-cols-[minmax(0,1.8fr)_minmax(300px,0.95fr)]">
        <div
          className={
            performanceQuery.isLoading
              ? 'animate-pulse opacity-60 pointer-events-none'
              : ''
          }
        >
          <InventoryDashboardPerformanceCard snapshot={snapshot} />
        </div>
        <div
          className={
            locationValuesQuery.isLoading
              ? 'animate-pulse opacity-60 pointer-events-none'
              : ''
          }
        >
          <InventoryDashboardLocationValueCard rows={snapshot.locationValues} />
        </div>
      </div>

      <div className="grid gap-4 xl:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)]">
        <div
          className={
            lowStockQuery.isLoading
              ? 'animate-pulse opacity-60 pointer-events-none'
              : ''
          }
        >
          <InventoryDashboardLowStockCard rows={snapshot.lowStock} />
        </div>
        <div
          className={
            recentActivityQuery.isLoading
              ? 'animate-pulse opacity-60 pointer-events-none'
              : ''
          }
        >
          <InventoryDashboardRecentActivityCard
            rows={snapshot.recentActivity}
          />
        </div>
      </div>
    </div>
  );
}
